import { useState, useMemo } from 'react';
import type { ReactNode } from 'react';
import clsx from 'clsx';

import { Card, Input } from '@lib/ui';
import { Table, THead, TBody, Tr, Th, Td } from '@lib/ui';
import { Pagination, PaginationItems, PaginationNext, PaginationPrevious } from '@lib/ui';

export interface DataTableColumn<T> {
  /** Unique key for the column */
  key: string;
  /** Column header label */
  header: string;
  /** Function to render cell content */
  render?: (row: T, index: number) => ReactNode;
  /** Property key to access from row data */
  accessor?: keyof T;
  /** Column width */
  width?: string;
  /** Whether the column is sortable */
  sortable?: boolean;
  /** Text alignment */
  align?: 'left' | 'center' | 'right';
}

export interface DataTableProps<T> {
  /** Array of data to display */
  data: T[];
  /** Column definitions */
  columns: DataTableColumn<T>[];
  /** Number of rows per page */
  pageSize?: number;
  /** Whether to show search input */
  searchable?: boolean;
  /** Placeholder for search input */
  searchPlaceholder?: string;
  /** Function to filter data based on search query */
  searchFilter?: (row: T, query: string) => boolean;
  /** Whether to show zebra striping */
  zebra?: boolean;
  /** Whether to highlight rows on hover */
  hoverable?: boolean;
  /** Whether to make header sticky */
  stickyHeader?: boolean;
  /** Whether to use dense styling */
  dense?: boolean;
  /** Title for the table card */
  title?: string;
  /** Additional actions to render in the header */
  headerActions?: ReactNode;
  /** Additional className for the container */
  className?: string;
  /** Function to get unique key for each row */
  getRowKey?: (row: T, index: number) => string | number;
  /** Empty state message */
  emptyMessage?: string;
}

export function DataTable<T extends Record<string, unknown>>({
  data,
  columns,
  pageSize = 10,
  searchable = true,
  searchPlaceholder = 'Search...',
  searchFilter,
  zebra = true,
  hoverable = true,
  stickyHeader = false,
  dense = false,
  title,
  headerActions,
  className,
  getRowKey,
  emptyMessage = 'No data available',
}: DataTableProps<T>) {
  const [searchQuery, setSearchQuery] = useState('');
  const [currentPage, setCurrentPage] = useState(1);

  // Filter data based on search query
  const filteredData = useMemo(() => {
    if (!searchQuery.trim()) return data;

    if (searchFilter) {
      return data.filter((row) => searchFilter(row, searchQuery));
    }

    // Default search: check all string values
    const query = searchQuery.toLowerCase();
    return data.filter((row) =>
      Object.values(row).some(
        (value) => typeof value === 'string' && value.toLowerCase().includes(query)
      )
    );
  }, [data, searchQuery, searchFilter]);

  // Calculate pagination
  const totalPages = Math.ceil(filteredData.length / pageSize);
  const startIndex = (currentPage - 1) * pageSize;
  const paginatedData = filteredData.slice(startIndex, startIndex + pageSize);

  // Reset to first page when search changes
  const handleSearchChange = (value: string) => {
    setSearchQuery(value);
    setCurrentPage(1);
  };

  return (
    <Card className={clsx('overflow-hidden w-fit', className)}>
      {(title || searchable || headerActions) && (
        <div className="flex flex-col gap-4 p-4 sm:flex-row sm:items-center sm:justify-between">
          {title && (
            <h3 className="text-lg font-medium text-gray-800 dark:text-dark-100">{title}</h3>
          )}
          <div className="flex flex-1 items-center justify-end gap-3">
            {searchable && (
              <Input
                placeholder={searchPlaceholder}
                value={searchQuery}
                onChange={(e) => handleSearchChange(e.target.value)}
                className="w-full sm:w-64"
                prefix={
                  <svg
                    xmlns="http://www.w3.org/2000/svg"
                    fill="none"
                    viewBox="0 0 24 24"
                    strokeWidth={1.5}
                    stroke="currentColor"
                    className="size-4"
                  >
                    <path
                      strokeLinecap="round"
                      strokeLinejoin="round"
                      d="m21 21-5.197-5.197m0 0A7.5 7.5 0 1 0 5.196 5.196a7.5 7.5 0 0 0 10.607 10.607Z"
                    />
                  </svg>
                }
              />
            )}
            {headerActions}
          </div>
        </div>
      )}

      <div className="overflow-x-auto">
        <Table zebra={zebra} hoverable={hoverable} sticky={stickyHeader} dense={dense}>
          <THead>
            <Tr>
              {columns.map((column) => (
                <Th
                  key={column.key}
                  style={{ width: column.width }}
                  className={clsx(
                    column.align === 'center' && 'text-center',
                    column.align === 'right' && 'text-right'
                  )}
                >
                  {column.header}
                </Th>
              ))}
            </Tr>
          </THead>
          <TBody>
            {paginatedData.length === 0 ? (
              <Tr>
                <Td colSpan={columns.length} className="text-center py-8 text-gray-500">
                  {emptyMessage}
                </Td>
              </Tr>
            ) : (
              paginatedData.map((row, index) => {
                const rowKey = getRowKey ? getRowKey(row, startIndex + index) : startIndex + index;
                return (
                  <Tr key={rowKey}>
                    {columns.map((column) => (
                      <Td
                        key={column.key}
                        className={clsx(
                          column.align === 'center' && 'text-center',
                          column.align === 'right' && 'text-right'
                        )}
                      >
                        {column.render
                          ? column.render(row, startIndex + index)
                          : column.accessor
                            ? String(row[column.accessor] ?? '')
                            : null}
                      </Td>
                    ))}
                  </Tr>
                );
              })
            )}
          </TBody>
        </Table>
      </div>

      {totalPages > 1 && (
        <div className="flex items-center justify-between border-t border-gray-200 dark:border-dark-500 p-4">
          <p className="text-sm text-gray-500 dark:text-dark-300">
            Showing {startIndex + 1} to {Math.min(startIndex + pageSize, filteredData.length)} of{' '}
            {filteredData.length} entries
          </p>
          <Pagination
            total={totalPages}
            value={currentPage}
            onChange={setCurrentPage}
            className="items-center gap-1"
          >
            <PaginationPrevious />
            <PaginationItems />
            <PaginationNext />
          </Pagination>
        </div>
      )}
    </Card>
  );
}

DataTable.displayName = 'DataTable';
